import { useEffect, useRef, useState } from 'react';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const serviceOptions = [
  'Kita & Kindergarten',
  'School Catering',
  'Corporate Catering',
  'Individual Meal Plan',
];

const mealRanges = [
  '1 – 10 meals/day',
  '11 – 50 meals/day',
  '51 – 100 meals/day',
  '100+ meals/day',
];

const contactDetails = [
  { icon: 'ri-phone-line', label: 'Phone', value: '+49 (0) 123 456 789' },
  { icon: 'ri-mail-line', label: 'Email', value: 'hello@lunch4you.de' },
  { icon: 'ri-map-pin-line', label: 'Address', value: 'Musterstraße 12, 10115 Berlin, Germany' },
  { icon: 'ri-time-line', label: 'Office Hours', value: 'Mon – Fri, 8:00 – 17:00' },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ContactForm() {
  const { ref, inView } = useInView();
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d6ufso7b245v7h34obeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-white overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-50 border border-primary-200 rounded-full mb-4">
            <i className="ri-mail-send-line text-primary-500 text-sm"></i>
            <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Get In Touch</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Let's Cook Something{' '}
            <span className="bg-gradient-to-r from-primary-500 to-pink-400 bg-clip-text text-transparent">Together</span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base">
            Whether you're a school, kindergarten, company or individual — tell us what you need and we'll get back to you within one business day.
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">

          {/* ── Left: Info Panel ── */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>

            {/* Hero image */}
            <div className="w-full h-52 rounded-2xl overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=friendly%20professional%20catering%20team%20smiling%20together%20in%20modern%20kitchen%20holding%20fresh%20food%2C%20diverse%20group%20of%20chefs%20and%20staff%2C%20warm%20natural%20light%2C%20clean%20white%20kitchen%20background%2C%20teamwork%20and%20hospitality%2C%20authentic%20professional%20photography&width=700&height=420&seq=contact-team-001&orientation=landscape"
                alt="Our team"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-2">We'd love to hear from you</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              From setting up school catering contracts to individual meal subscriptions — our team is ready to help you find the right plan.
            </p>

            {/* Contact details */}
            <ul className="space-y-4 mb-8">
              {contactDetails.map((item) => (
                <li key={item.label} className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-50 flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-primary-500 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium leading-none mb-0.5">{item.label}</p>
                    <p className="text-gray-800 text-sm font-semibold">{item.value}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: '< 24h', label: 'Response time' },
                { value: '3.5M+', label: 'Meals served / year' },
                { value: '100%', label: 'Fresh daily' },
                { value: '15+', label: 'Years experience' },
              ].map((s) => (
                <div key={s.label} className="bg-primary-50 rounded-xl px-4 py-3 border border-primary-100">
                  <p className="text-primary-600 font-extrabold text-lg leading-none">{s.value}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Form ── */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center text-center py-20 px-8 bg-primary-50 rounded-3xl border-2 border-dashed border-primary-200">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-500 rounded-full mb-5">
                  <i className="ri-check-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-sm max-w-sm">
                  Thanks for reaching out! Our team will get back to you within one business day.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  {/* Full Name */}
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Full Name <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="e.g. Maria Müller"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Email <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>

                  {/* Phone */}
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+49 ..."
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>

                  {/* Organization */}
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      School / Organization
                    </label>
                    <input
                      type="text"
                      name="organization"
                      placeholder="e.g. Grundschule Mitte"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>

                  {/* Service Type */}
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Service Type <span className="text-primary-500">*</span>
                    </label>
                    <select
                      name="service_type"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer appearance-none"
                    >
                      <option value="" disabled>Select a service…</option>
                      {serviceOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  {/* Meals per day */}
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Meals per Day
                    </label>
                    <select
                      name="meals_per_day"
                      defaultValue=""
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer appearance-none"
                    >
                      <option value="" disabled>Approx. quantity…</option>
                      {mealRanges.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your needs, special dietary requirements, preferred start date…"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  />
                  <p className={`text-right text-xs mt-1 ${charCount >= 450 ? 'text-primary-500 font-semibold' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                {/* Error */}
                {status === 'error' && (
                  <p className="text-red-500 text-sm mb-4 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    Something went wrong. Please try again.
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-primary-500 text-white font-bold text-base rounded-2xl hover:bg-primary-600 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      Send Inquiry
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 mt-4">
                  We typically respond within one business day. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
